import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'Components/Atoms/Select/Select';
import useProvidersConnections from 'Hooks/useProvidersConnections';
import clsx from 'clsx';
import Loading from 'Components/Atoms/Loading/Loading';
import { importTransactions, mapCSVImport } from 'Services/transactions';
import useDebounceLastValue from 'Hooks/useDebounceLastValue';
import { Trans, useTranslation } from 'react-i18next';
import ImportFile from 'Components/Organisms/ImportFile/ImportFile';
import { DOCUMENTATION_LINKS, DRAWER_STACK } from 'Utils/constants';
import useDrawerStack from 'Hooks/useDrawerStack';
import OutlineAdd from 'Components/Atoms/OutlineIcons/OutlineAdd';
import CreateTransactionSuccess from '../CreateTransactionSuccess/CreateTransactionSuccess';

const SCREENS = {
    FORM: 'FORM',
    SUCCESS: 'SUCCESS',
};

const NORMA43_FILE_EXTENSIONS = ['.n43', '.txt'];

export default function CreateTransactionImportCSVDrawer(props) {
    const { onClose, providerSelectedId: initialProviderSelectedId = null, blockProvider = false } = props;

    const { open } = useDrawerStack();
    const [providerSelectedId, setProviderSelectedId] = useState(initialProviderSelectedId);

    const [provider, setProvider] = useState('');
    const [product, setProduct] = useState('');
    const [processing, setProcessing] = useState(false);
    const [screen, setScreen] = useState(SCREENS.FORM);

    const { validProvidersConnections: providersConnections, isLoading, manualProviders } = useProvidersConnections();

    const { t, i18n } = useTranslation('providers');

    const documentationLink = DOCUMENTATION_LINKS.IMPORT_TRANSACTIONS[i18n.language];

    const handleOpenAddProvider = () => {
        open(DRAWER_STACK.ADD_MANUAL_PROVIDER, {
            title: t('manual-bank.add-manual-bank'),
            onSubmit: (newProvider) => {
                setProviderSelectedId(newProvider.id);
            },
        });
    };

    const manualProvidersOptions = useMemo(
        () =>
            manualProviders.map((item) => ({
                value: item.id,
                label: item.name,
            })),
        [manualProviders],
    );

    const productsOptions = useMemo(
        () =>
            manualProviders
                .find((item) => item.id === provider)
                ?.products.map((item) => ({
                    value: item.id,
                    label: item.name,
                })) || [],
        [provider, manualProviders],
    );

    useEffect(() => {
        if (manualProviders.length > 0) {
            if (providerSelectedId) {
                const providerFound = manualProviders.find((item) => item.id === providerSelectedId);
                if (providerFound) {
                    setProvider(providerFound.id);
                    const [productItem] = providerFound.products;
                    setProduct(productItem.id);
                }
            } else {
                setProvider(manualProviders[0].id);
                const [productItem] = manualProviders[0].products;
                setProduct(productItem.id);
            }
        }
    }, [manualProviders, providerSelectedId]);

    const handleChangeProvider = (value) => {
        if (value === provider) return;

        setProvider(value);
        const providerSelected = providersConnections.find((item) => item.id === value);
        if (providerSelected) {
            setProduct(providerSelected.products[0]?.id ?? null);
        } else {
            setProduct(null);
        }
    };

    const handleClickAddProvider = (ev) => {
        ev.preventDefault();
        handleOpenAddProvider();
    };

    const handleSuccess = () => {
        setScreen(SCREENS.SUCCESS);
    };

    const hasProducts = useDebounceLastValue(productsOptions.length > 0, { initialValue: true, delay: 100 });

    const fields = useMemo(
        () => [
            {
                id: 'description',
                label: t('manual-bank.concept'),
                required: true,
                deduplicationColumns: false,
            },
            {
                id: 'amount',
                label: t('manual-bank.amount'),
                deduplicationColumns: false,
                required: true,
            },
            {
                id: 'date_booked',
                label: t('manual-bank.date'),
                deduplicationColumns: false,
                required: true,
            },
            {
                id: 'id',
                label: t('manual-bank.identifier'),
                required: false,
                deduplicationColumns: true,
            },
            {
                id: 'comment',
                label: t('manual-bank.comment'),
                required: false,
                deduplicationColumns: false,
            },
            {
                id: 'balance',
                label: t('manual-bank.balance'),
                required: false,
                deduplicationColumns: false,
            },
            {
                id: 'date_executed',
                label: t('manual-bank.execution-date'),
                required: false,
                deduplicationColumns: false,
            },
        ],
        [t],
    );

    if (isLoading) {
        return (
            <div className="text-center py-6">
                <Loading />
            </div>
        );
    }

    if (screen === SCREENS.SUCCESS) {
        return <CreateTransactionSuccess onClose={onClose} />;
    }

    return (
        <ImportFile
            texts={{
                importDescription: (
                    <div className=" text-gray-500 text-sm">
                        <p className="mb-1">{t('manual-bank.import-transactions-description')}</p>
                        <p>
                            <a
                                href={documentationLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-primary underline hover:no-underline"
                            >
                                {t('manual-bank.see-documentation')}
                            </a>
                        </p>
                    </div>
                ),
                importButton: t('manual-bank.add-transactions'),
                mapperDescription: t('manual-bank.import-transactions-mapper'),
                mapperButton: t('manual-bank.add-transactions'),
            }}
            fields={fields}
            extraFileExtensions={NORMA43_FILE_EXTENSIONS}
            dropText="drop-csv-xlsx-n43-file"
            onClose={onClose}
            importService={(file) => importTransactions(product, file)}
            mapService={mapCSVImport}
            processing={processing}
            onChangeProcessing={setProcessing}
            onSuccess={handleSuccess}
            disabled={!hasProducts}
        >
            {!hasProducts && (
                <div className="text-gray-500 mb-6 py-5 text-center text-pretty text-sm border border-dashed rounded border-gray-300 bg-gray-50 px-4">
                    <Trans t={t} i18nKey="manual-bank.no-products">
                        Para crear una transacción, primero debe
                        <a
                            href="#"
                            onClick={handleClickAddProvider}
                            className="text-dark-blue underline hover:no-underline"
                        >
                            crear un banco manual
                        </a>
                    </Trans>
                </div>
            )}

            <div className="grid grid-cols-2 gap-4 mb-4">
                {!blockProvider && (
                    <Select
                        label={t('manual-bank.bank')}
                        value={provider}
                        onChange={handleChangeProvider}
                        options={manualProvidersOptions}
                        className="w-full"
                        actions={[
                            <button
                                key="add-provider"
                                type="button"
                                onClick={handleOpenAddProvider}
                                className="flex items-center gap-2 w-full px-3 py-2 rounded-sm hover:bg-gray-100 transition-colors"
                            >
                                <OutlineAdd size={20} color="currentColor" />
                                <span>{t('manual-bank.add-manual-bank')}</span>
                            </button>,
                        ]}
                    />
                )}

                <Select
                    label={t('manual-bank.account')}
                    className={clsx('w-full', blockProvider && 'col-span-2')}
                    value={product}
                    onChange={setProduct}
                    options={productsOptions}
                    disabled={!hasProducts}
                />
            </div>
        </ImportFile>
    );
}

CreateTransactionImportCSVDrawer.propTypes = {
    onClose: PropTypes.func.isRequired,
    providerSelectedId: PropTypes.string,
    blockProvider: PropTypes.bool,
};
